import React, { useCallback, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import AlertMessage from "../../../components/AlertMessage";
import LoadingIcon from "../../../components/icons/LoadingIcon";
import { useAuthentication } from "../../../context/authentication";
import { useDiscordChannels } from "../../../context/discordChannels";
import { useToast } from "../../../context/toast";
import { useWallets } from "../../../context/wallets";
import { Entity } from "../../../utils/constants";
import { h3ToParentMemoized } from "../../../utils/memoizedMethods";

const DiscordChannelTab: React.FC<RouteComponentProps<{ h3Index: string }>> = (
  props
) => {
  const { channels, requestChannel } = useDiscordChannels();
  const { wrapActionWithAuth, authenticationState } = useAuthentication();
  const { verifiedWalletsCount } = useWallets();
  const { addToast } = useToast();
  const [isFetching, setIsFetching] = useState(false);

  const channel = useMemo(
    () => channels[props.match.params.h3Index],
    [channels, props.match.params.h3Index]
  );

  const requestHexChannel = useCallback(async () => {
    if (verifiedWalletsCount < 1) {
      addToast({
        type: "warning",
        title: "Please verify a wallet first"
      });
      return;
    }

    setIsFetching(true);

    const apiResponse = await requestChannel({
      linked_id: props.match.params.h3Index,
      name: props.match.params.h3Index,
      type: Entity.hex,
      h3_index_r4: h3ToParentMemoized(props.match.params.h3Index, 4)
    });

    if (!apiResponse.status) {
      addToast({
        type: "error",
        title: "Hex channel creation failed",
        message: apiResponse.message
      });

      setIsFetching(false);

      return;
    }

    const a = document.createElement("a");

    a.href = `https://discord.com/channels/874255050929745930/${apiResponse.data.discord_id}`;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.click();

    setIsFetching(false);
  }, [
    addToast,
    props.match.params.h3Index,
    requestChannel,
    verifiedWalletsCount
  ]);

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      {authenticationState !== "authenticated" && (
        <AlertMessage
          className="mt-2"
          type="warning"
          title="Login is required before you can start collaborating"
        />
      )}

      {authenticationState === "authenticated" &&
        !channel &&
        verifiedWalletsCount < 1 && (
          <AlertMessage
            className="mt-2"
            type="warning"
            title="Minimum one wallet verification is required"
            message="Just like any other communication platform, Hotspotty's collaborative hex channels are susceptible to abuse from bad actors. Its purpose is to facilitate collaboration between hotspot owners to improve the People's Network together. For this reason we require at least one wallet verification by your workspace in order to create hex channels."
            collapsible
          />
        )}

      <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
        Communicate and collaborate with your local Helium community.
      </p>

      <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
        Work with other willing hotspot owners and partners, join forces, learn
        from each other, solve problems and continually improve the people’s
        network – together!
      </p>

      <div className="mt-6">
        {channel && authenticationState === "authenticated" ? (
          <a
            href={`https://discord.com/channels/874255050929745930/${channel.discord_id}`}
            target="_blank"
            rel="noreferrer"
            className="px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Discuss in Discord
          </a>
        ) : (
          <button
            onClick={wrapActionWithAuth(requestHexChannel)}
            type="button"
            disabled={isFetching}
            className="flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-default"
          >
            {isFetching && (
              <LoadingIcon className="animate-spin mr-2 h-3 w-3" />
            )}
            <span>
              {isFetching ? "Preparing channel" : "Discuss in Discord"}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default DiscordChannelTab;
